/**
 * Agora SDK Lazy Loader
 * 
 * This utility handles lazy loading of the Agora RTC SDK to improve
 * initial page load performance. The SDK is only downloaded when needed
 * (video call initiated) but can be preloaded in the background after
 * user authentication.
 */

// Singleton promise to prevent multiple downloads
let agoraLoadPromise: Promise<any> | null = null;

// Track if preload hint has been added
let preloadHintAdded = false;

/**
 * Load the Agora SDK lazily
 * Returns immediately if SDK is already loaded
 * Returns cached promise if load is in progress
 */
export const loadAgoraSDK = (): Promise<any> => {
  // Already loaded
  if (typeof window !== 'undefined' && window.AgoraRTC) {
    return Promise.resolve(window.AgoraRTC);
  }

  // Load in progress, return existing promise
  if (agoraLoadPromise) {
    return agoraLoadPromise;
  }

  // Start loading
  agoraLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://download.agora.io/sdk/release/AgoraRTC_N.js';
    script.async = true;
    
    script.onload = () => {
      resolve(window.AgoraRTC);
    };
    
    script.onerror = (error) => {
      agoraLoadPromise = null; // Reset so retry is possible
      reject(new Error('Failed to load Agora SDK'));
    };
    
    document.head.appendChild(script);
  });

  return agoraLoadPromise;
};

/**
 * Add a preload hint for the Agora SDK
 * This tells the browser to fetch the script in the background
 * without executing it, so it's ready when needed
 */
const addPreloadHint = (): void => {
  if (preloadHintAdded || typeof document === 'undefined') {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = 'https://download.agora.io/sdk/release/AgoraRTC_N.js';
  document.head.appendChild(link);
  preloadHintAdded = true;
};

/**
 * Preload the Agora SDK in the background after authentication
 * Uses requestIdleCallback to avoid blocking main thread
 * Falls back to setTimeout for browsers without requestIdleCallback
 */
export const preloadAgoraSDK = (): void => {
  // Skip if already loaded
  if (typeof window !== 'undefined' && window.AgoraRTC) {
    return;
  }

  // Add preload hint immediately (non-blocking)
  addPreloadHint();

  // Load SDK during idle time
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(
      () => {
        loadAgoraSDK().catch(() => {
          // Silent fail for preload - will retry when actually needed
        });
      },
      { timeout: 5000 } // Max 5 seconds delay
    );
  } else {
    // Fallback for Safari and older browsers
    setTimeout(() => {
      loadAgoraSDK().catch(() => {
        // Silent fail for preload
      });
    }, 2000);
  }
};

/**
 * Check if Agora SDK is currently loaded
 */
export const isAgoraSDKLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!window.AgoraRTC;
};
